/**
 * Checkout Resource Client
 * Axios-based API client for checkout endpoints
 */
import { PROMOTION_APPLIED, PROMOTION_REMOVED } from './promotionEvents';
export class CheckoutResource {
    constructor(apiClient, bus) {
        this.apiClient = apiClient;
        this.bus = bus;
    }
    /**
     * Initialize a new checkout session (sync mode)
     * Response time: 2-5 seconds (full processing)
     */
    async initCheckout(params) {
        // Pass all params including customerId to prevent duplicate customer creation
        return this.apiClient.post('/api/v1/checkout/session/init', params);
    }
    /**
     * Initialize a new checkout session (async mode) ⚡
     * Response time: ~50ms (20-50x faster!)
     *
     * Returns checkoutToken immediately, background job completes processing.
     * Use getCheckout() to fetch full session data - it auto-waits for completion.
     *
     * @example
     * // Fast init
     * const { checkoutToken } = await checkout.initCheckoutAsync(params);
     *
     * // Redirect user immediately
     * window.location.href = `/checkout/${checkoutToken}/op`;
     *
     * // By the time page loads, background processing is usually complete
     */
    async initCheckoutAsync(params) {
        return this.apiClient.post('/api/v1/checkout/session/init-async', params);
    }
    /**
     * Preload checkout session (ultra-fast background pre-computation) ⚡⚡⚡
     *
     * This is the recommended way to handle cart changes or "Buy Now" intent.
     * It pre-computes everything (checkoutToken, navigation URL, CMS session)
     * before the user even clicks the checkout button.
     *
     * The SDK automatically gets funnelSessionId from FunnelClient if provided.
     * Only FunnelClient knows how to properly extract funnelSessionId (from state, URL, cookies, etc.)
     *
     * @param params - Checkout and funnel parameters
     * @param getFunnelSessionId - Optional function to get funnelSessionId from FunnelClient
     *                             This maintains separation of concerns - only FunnelClient knows how to get it
     *
     * @returns { checkoutToken, customerId, navigationUrl }
     */
    async preloadCheckout(params, getFunnelSessionId) {
        // ⚡ GET FUNNEL SESSION ID: Only FunnelClient knows how to properly get it
        // Priority: explicit param > FunnelClient > backend fallback (via currentUrl)
        let funnelSessionId = params.funnelSessionId;
        if (!funnelSessionId && getFunnelSessionId) {
            // Let FunnelClient handle extraction (from state, URL, cookies, etc.)
            funnelSessionId = getFunnelSessionId() || undefined;
        }
        // Format navigationEvent if it's a string
        const navigationEvent = typeof params.navigationEvent === 'string'
            ? { type: params.navigationEvent }
            : params.navigationEvent;
        // Build request - backend will also try to extract from currentUrl if not provided
        const requestParams = {
            ...params,
            ...(funnelSessionId && { funnelSessionId }),
            ...(navigationEvent && { navigationEvent }),
            // Ensure currentUrl is always set for backend extraction fallback
            currentUrl: params.currentUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
        };
        return this.apiClient.post('/api/v1/checkout/session/preload', requestParams);
    }
    /**
     * Check async checkout processing status (instant, no waiting)
     * Perfect for polling or checking if background job completed
     */
    async checkAsyncStatus(checkoutToken) {
        return this.apiClient.get(`/api/public/v1/checkout/async-status/${checkoutToken}`);
    }
    /**
     * Get checkout session by token
     *
     * SDK automatically waits for async completion (skipAsyncWait=false) for seamless UX.
     * This ensures async checkout sessions are fully processed before returning data.
     *
     * If you need to skip waiting (e.g., for manual polling), use getCheckoutRaw() instead.
     */
    async getCheckout(checkoutToken, currency) {
        const queryParams = new URLSearchParams();
        if (currency) {
            queryParams.set('currency', currency);
        }
        // SDK explicitly waits for async completion (default is skip=true for retro compat)
        queryParams.set('skipAsyncWait', 'false');
        const url = `/api/v1/checkout-sessions/${checkoutToken}/v2${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return this.apiClient.get(url);
    }
    /**
     * Get checkout session by token without waiting for async completion
     * Useful for manual polling scenarios
     */
    async getCheckoutRaw(checkoutToken, currency) {
        const queryParams = new URLSearchParams();
        if (currency) {
            queryParams.set('currency', currency);
        }
        queryParams.set('skipAsyncWait', 'true');
        const url = `/api/v1/checkout-sessions/${checkoutToken}/v2${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return this.apiClient.get(url);
    }
    /**
     * Update checkout address
     */
    async updateAddress(checkoutSessionId, data) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/address`, {
            data,
        });
    }
    /**
     * Set checkout info
     */
    async setCheckoutInfo(checkoutSessionId, data) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/checkout-info`, {
            shippingAddress: data.shippingAddress,
            billingAddress: data.differentBillingAddress ? data.billingAddress : data.shippingAddress,
            differentBillingAddress: data.differentBillingAddress || false,
        });
    }
    /**
     * Apply promotion code
     */
    async applyPromotionCode(checkoutSessionId, code) {
        const result = await this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions/apply`, {
            code,
        });
        if (result.success) {
            void this.bus?.emit(PROMOTION_APPLIED, { checkoutSessionId });
        }
        return result;
    }
    /**
     * Remove promotion
     */
    async removePromotion(checkoutSessionId, promotionId) {
        const result = await this.apiClient.delete(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions/${promotionId}`);
        if (result.success) {
            void this.bus?.emit(PROMOTION_REMOVED, { checkoutSessionId });
        }
        return result;
    }
    /**
     * Get applied promotions
     */
    async getAppliedPromotions(checkoutSessionId) {
        return this.apiClient.get(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions`);
    }
    /**
     * Replace all session line items (full rewrite)
     */
    async replaceSessionLineItems(checkoutSessionId, lineItems) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items`, {
            lineItems,
        });
    }
    /**
     * Update line items
     */
    async updateLineItems(checkoutSessionId, lineItems) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items`, {
            lineItems,
        });
    }
    /**
     * Add line items
     */
    async addLineItems(checkoutSessionId, lineItems) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/add`, {
            lineItems,
        });
    }
    /**
     * Remove line items
     */
    async removeLineItems(checkoutSessionId, lineItems) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/remove`, {
            lineItems,
        });
    }
    /**
     * Set item quantity
     */
    async setItemQuantity(checkoutSessionId, variantId, quantity, priceId) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/set-quantity`, {
            variantId,
            quantity,
            priceId,
        });
    }
    /**
     * Toggle order bump
     */
    async toggleOrderBump(checkoutSessionId, orderBumpOfferId, selected) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/toggle-order-bump`, {
            orderBumpOfferId,
            selected,
        });
    }
    /**
     * Update customer
     */
    async updateCustomer(checkoutSessionId, data) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/customer`, data);
    }
    /**
     * Update customer and session info
     */
    async updateCustomerAndSessionInfo(checkoutSessionId, data) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/customer-and-session-info`, data);
    }
    async previewCheckoutSession(params) {
        return this.apiClient.post('/api/v1/checkout-sessions/preview', params);
    }
    /**
     * Set selected payment method for a checkout session
     */
    async setPaymentMethod(checkoutSessionId, paymentMethodName) {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/payment-method`, { paymentMethodName });
    }
}
